<template>
  <div class="gmv-chart">
    <div class="chart-container" ref="chartContainer">
      <svg ref="svg"></svg>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';

export default {
  name: "GMVChart",
  props: {
    chartData: {
      type: Array,
      required: true,
    },
    height: {
      type: Number,
      default: 350,
    },
  },
  data() {
    return {
      svg: null,
      width: 0,
      margin: { top: 20, right: 70, bottom: 80, left: 80 },
      isMobile: false,
    };
  },
  computed: {
    chartHeight() {
      return this.height - this.margin.top - this.margin.bottom;
    },
  },
  mounted() {
    this.initChart();
    this.renderChart();

    // Add window resize listener
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  watch: {
    chartData: {
      handler() {
        this.renderChart();
      },
      deep: true,
    },
  },
  methods: {
    initChart() {
      const container = this.$refs.chartContainer;
      // Use window width for responsive detection instead of container width
      this.isMobile = window.innerWidth < 800;
      const isVeryNarrow = window.innerWidth < 400;

      // Set responsive margins with better spacing for different screen sizes
      if (isVeryNarrow) {
        // Very narrow screens - minimize margins while keeping chart visible
        this.margin = { top: 10, right: 30, bottom: 40, left: 40 };
      } else if (this.isMobile) {
        this.margin = { top: 15, right: 40, bottom: 60, left: 60 };
      } else {
        // Increase margins for desktop to accommodate full currency formatting and pulsing dot
        this.margin = { top: 20, right: 70, bottom: 40, left: 80 };
      }

      this.width = container.offsetWidth - this.margin.left - this.margin.right;

      // Ensure minimum width with flexible constraints for very narrow screens
      if (isVeryNarrow && this.width < 200) {
        this.width = Math.max(20, container.offsetWidth - 60); // At least 200px or container minus minimal margins
      } else if (this.isMobile && this.width < 250) {
        this.width = 250;
      } else if (!this.isMobile && this.width < 400) {
        this.width = 400;
      }

      // Clear any existing SVG
      d3.select(this.$refs.svg).selectAll("*").remove();

      this.svg = d3.select(this.$refs.svg)
        .attr("width", this.width + this.margin.left + this.margin.right)
        .attr("height", this.height + this.margin.top + this.margin.bottom);

      // Create main group
      this.g = this.svg.append("g")
        .attr("transform", `translate(${this.margin.left},${this.margin.top})`);

      // Create gradient definitions
      this.createGradients();
    },

    createGradients() {
      const defs = this.svg.append("defs");

      // Create purple gradient for area fill with more color stops
      const areaGradient = defs.append("linearGradient")
        .attr("id", "area-gradient")
        .attr("gradientUnits", "userSpaceOnUse")
        .attr("x1", 0).attr("y1", 0)
        .attr("x2", 0).attr("y2", this.chartHeight);

      areaGradient.selectAll("stop")
        .data([
          { offset: "0%", color: "rgba(99,85,255, 0.9)" },
          { offset: "20%", color: "rgba(99,85,255, 0.7)" },
          { offset: "40%", color: "rgba(99,85,255, 0.5)" },
          { offset: "60%", color: "rgba(99,85,255, 0.3)" },
          { offset: "80%", color: "rgba(99,85,255, 0.1)" },
          { offset: "100%", color: "rgba(99,85,255, 0.02)" }
        ])
        .enter().append("stop")
        .attr("offset", d => d.offset)
        .attr("stop-color", d => d.color);

      // Create animated glow effect gradient for line
      const glowGradient = defs.append("linearGradient")
        .attr("id", "line-glow")
        .attr("gradientUnits", "userSpaceOnUse")
        .attr("x1", 0).attr("y1", 0)
        .attr("x2", this.width).attr("y2", 0);

      glowGradient.selectAll("stop")
        .data([
          { offset: "0%", color: "#6355FF" },
          { offset: "25%", color: "#6355FF" },
          { offset: "50%", color: "#6355FF" },
          { offset: "75%", color: "#6355FF" },
          { offset: "100%", color: "#6355FF" }
        ])
        .enter().append("stop")
        .attr("offset", d => d.offset)
        .attr("stop-color", d => d.color);

      // Enhanced glow filter with multiple layers
      const filter = defs.append("filter")
        .attr("id", "glow")
        .attr("width", "300%")
        .attr("height", "300%")
        .attr("x", "-100%")
        .attr("y", "-100%");

      // Create multiple glow layers for more dramatic effect
      filter.append("feGaussianBlur")
        .attr("stdDeviation", this.isMobile ? "2" : "4")
        .attr("result", "glow1");

      filter.append("feGaussianBlur")
        .attr("stdDeviation", this.isMobile ? "4" : "8")
        .attr("result", "glow2");

      const feMerge = filter.append("feMerge");
      feMerge.append("feMergeNode").attr("in", "glow2");
      feMerge.append("feMergeNode").attr("in", "glow1");
      feMerge.append("feMergeNode").attr("in", "SourceGraphic");

      // Add subtle background pattern
      const pattern = defs.append("pattern")
        .attr("id", "background-pattern")
        .attr("patternUnits", "userSpaceOnUse")
        .attr("width", 60)
        .attr("height", 60);

      pattern.append("rect")
        .attr("width", 60)
        .attr("height", 60)
        .attr("fill", "#ffffff");

      pattern.append("circle")
        .attr("cx", 30)
        .attr("cy", 30)
        .attr("r", 1)
        .attr("fill", "rgba(99,85,255, 0.1)");
    },

    renderChart() {
      if (!this.chartData || this.chartData.length === 0) return;

      // Process data
      const data = this.chartData.map(d => ({
        date: new Date(process.env.NODE_ENV == 'development' ? d.date : d.date),
        value: process.env.NODE_ENV == 'development' ? d.total_gmv_lifetime :  d.total_gmv_lifetime
      }));

      // Create scales
      const xScale = d3.scaleTime()
        .domain(d3.extent(data, d => d.date))
        .range([0, this.width]);

      const yScale = d3.scaleLinear()
        .domain([0, d3.max(data, d => d.value) * 1.1])
        .range([this.chartHeight, 0]);

      // Create line generator
      const line = d3.line()
        .x(d => xScale(d.date))
        .y(d => yScale(d.value))
        .curve(d3.curveCardinal.tension(0.3));

      // Create area generator
      const area = d3.area()
        .x(d => xScale(d.date))
        .y0(this.chartHeight)
        .y1(d => yScale(d.value))
        .curve(d3.curveCardinal.tension(0.3));

      // Clear previous content
      this.g.selectAll(".chart-content").remove();

      const chartGroup = this.g.append("g").attr("class", "chart-content");

      // Add subtle background pattern
      chartGroup.append("rect")
        .attr("class", "background-pattern")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", this.width)
        .attr("height", this.chartHeight)
        .attr("fill", "url(#background-pattern)")
        .style("opacity", 0)
        .transition()
        .duration(1000)
        .style("opacity", 1);

      // Add area with animation
      const areaPath = chartGroup.append("path")
        .datum(data)
        .attr("class", "area")
        .attr("fill", "url(#area-gradient)")
        .attr("d", area)
        .style("opacity", 0);

      // Add line with animation
      const linePath = chartGroup.append("path")
        .datum(data)
        .attr("class", "line")
        .attr("fill", "none")
        .attr("stroke", "url(#line-glow)")
        .attr("stroke-width", this.isMobile ? 3 : 4)
        .attr("filter", "url(#glow)")
        .attr("d", line);

      // Get line length for animation
      const totalLength = linePath.node().getTotalLength();

      // Animate line drawing with sophisticated easing
      linePath
        .attr("stroke-dasharray", totalLength + " " + totalLength)
        .attr("stroke-dashoffset", totalLength)
        .transition()
        .duration(2500)
        .ease(d3.easeCubicInOut)
        .attr("stroke-dashoffset", 0)
        .style('transform', 'translate(2px, -4px)')

      // Animate area fill with cascading effect
      areaPath
        .transition()
        .delay(800)
        .duration(2000)
        .ease(d3.easeCubicInOut)
        .style("opacity", 1)
        .style('transform', 'translate(2px, -4px)');

      // Add pulsing purple dot at the end of the line
      this.addPulsingDot(chartGroup, data, xScale, yScale);

      // Add axes
      this.addAxes(xScale, yScale);
    },

    addPulsingDot(chartGroup, data, xScale, yScale) {
      if (data.length === 0) return;

      // Get the last data point
      const lastPoint = data[data.length - 1];
      const x = xScale(lastPoint.date);
      const y = yScale(lastPoint.value);

      // Create a group for the pulsing dot
      const dotGroup = chartGroup.append("g").attr("class", "pulsing-dot");

      // Mobile responsive pulse sizes
      const dotSize = this.isMobile ? 4 : 6;
      const maxPulseOuter = this.isMobile ? 15 : 20;
      const maxPulseInner = this.isMobile ? 12 : 15;

      // Add multiple layers for the pulsing effect
      // Outer pulse ring
      const outerPulse = dotGroup.append("circle")
        .attr("cx", x)
        .attr("cy", y)
        .attr("r", 0)
        .attr("fill", "none")
        .attr("stroke", "#6355FF")
        .attr("stroke-width", this.isMobile ? 1.5 : 2)
        .attr("opacity", 0.8)

      // Inner pulse ring
      const innerPulse = dotGroup.append("circle")
        .attr("cx", x)
        .attr("cy", y)
        .attr("r", 0)
        .attr("fill", "none")
        .attr("stroke", "#6355FF")
        .attr("stroke-width", 1)
        .attr("opacity", 0.6)

      // Main dot
      const mainDot = dotGroup.append("circle")
        .attr("cx", x)
        .attr("cy", y)
        .attr("r", 0)
        .attr("fill", "#6355FF")
        .attr("filter", "url(#glow)")
        .style("opacity", 0)

      // Animate the main dot appearance after the line animation
      mainDot
        .transition()
        .delay(3000) // After line animation completes
        .duration(500)
        .ease(d3.easeBackOut.overshoot(1.7))
        .attr("r", dotSize)
        .style("opacity", 1);

      // Start pulsing animation after main dot appears
      setTimeout(() => {
        this.startPulsingAnimation(outerPulse, innerPulse, dotSize, maxPulseOuter, maxPulseInner);
      }, 3500);
    },

    startPulsingAnimation(outerPulse, innerPulse, dotSize, maxPulseOuter, maxPulseInner) {
      // Animate outer pulse
      const animateOuterPulse = () => {
        outerPulse
          .attr("r", dotSize)
          .attr("opacity", 0.8)
          .transition()
          .duration(2000)
          .ease(d3.easeCircleOut)
          .attr("r", maxPulseOuter)
          .attr("opacity", 0)
          .on("end", animateOuterPulse);
      };

      // Animate inner pulse (slightly faster)
      const animateInnerPulse = () => {
        innerPulse
          .attr("r", dotSize)
          .attr("opacity", 0.6)
          .transition()
          .duration(1500)
          .ease(d3.easeCircleOut)
          .attr("r", maxPulseInner)
          .attr("opacity", 0)
          .on("end", animateInnerPulse);
      };

      // Start both pulse animations with a slight delay between them
      animateOuterPulse();
      setTimeout(animateInnerPulse, 300);
    },

    addAxes(xScale, yScale) {
      // Remove existing axes
      this.g.selectAll(".axis").remove();

      // Responsive tick counts
      const xTicks = this.isMobile ? 4 : 8;
      const yTicks = this.isMobile ? 4 : 6;

      // Add X axis
      const xAxis = this.g.append("g")
        .attr("class", "axis x-axis")
        .attr("transform", `translate(0,${this.chartHeight})`)
        .call(d3.axisBottom(xScale)
          .tickFormat(d3.timeFormat("20%y"))
          .ticks(xTicks));

      // Style x-axis
      xAxis.selectAll("text")
        .style("font-family", "Inter, sans-serif")
        .style("font-size", this.isMobile ? "10px" : "11px")
        .style("fill", "#6B7280")
        .attr("transform", "rotate(0)")
        .style("letter-spacing", "-.3px")
        .style("text-anchor", "center");

      xAxis.selectAll("line")
        .style("stroke", "#6355FF");

      xAxis.select(".domain")
        .style("stroke", "#E5E7EB");

      // Add Y axis with responsive formatting
      const yAxis = this.g.append("g")
        .attr("class", "axis y-axis")
        .call(d3.axisLeft(yScale)
          .tickFormat(d => this.isMobile ? this.formatCurrencyMobile(d) : this.formatCurrency(d))
          .ticks(yTicks));

      // Style y-axis
      yAxis.selectAll("text")
        .style("font-family", "Inter, sans-serif")
        .style("font-size", this.isMobile ? "9px" : "10px")
        .style("fill", "#6B7280")
        .style("font-weight", "400")
        .style("letter-spacing", "-.5px")
        .attr("transform", this.isMobile ? "rotate(0)" : "rotate(0)")
        .style("text-anchor", "end");

      yAxis.selectAll("line")
        .style("stroke", "#F3F4F6")
        .style("stroke-dasharray", "3,3");

      yAxis.select(".domain")
        .style("stroke", "#E5E7EB");
    },

    showTooltip(event, d) {
      const tooltip = d3.select("body").append("div")
        .attr("class", "d3-tooltip")
        .style("position", "absolute")
        .style("background", "rgba(255, 255, 255, 0.95)")
        .style("backdrop-filter", "blur(10px)")
        .style("border", "1px solid rgba(99,85,255, 0.2)")
        .style("color", "#1F2937")
        .style("padding", this.isMobile ? "12px 16px" : "16px 20px")
        .style("border-radius", "12px")
        .style("font-family", "Inter, sans-serif")
        .style("font-size", this.isMobile ? "12px" : "14px")
        .style("pointer-events", "none")
        .style("box-shadow", "0 8px 32px rgba(0, 0, 0, 0.1)")
        .style("opacity", 0)
        .style("transform", "translateY(10px)")
        .style("transition", "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)");

      tooltip.html(`
        <div style="margin-bottom: 8px; font-weight: 600; color: #374151; font-size: ${this.isMobile ? '11px' : '13px'}; letter-spacing: 0.5px; text-transform: uppercase;">
          ${d.date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })}
        </div>
        <div style="color: #8B5CF6; font-weight: 700; font-size: ${this.isMobile ? '16px' : '18px'}; letter-spacing: -0.5px;">
          ${this.formatCurrency(d.value)}
        </div>
        <div style="margin-top: 4px; font-size: ${this.isMobile ? '10px' : '12px'}; color: #6B7280;">
          Total GMV Lifetime
        </div>
      `)
        .style("left", (event.pageX + 15) + "px")
        .style("top", (event.pageY - 60) + "px");

      // Animate tooltip appearance
      setTimeout(() => {
        tooltip
          .style("opacity", 1)
          .style("transform", "translateY(0px)");
      }, 10);
    },

    hideTooltip() {
      d3.selectAll(".d3-tooltip")
        .style("opacity", 0)
        .style("transform", "translateY(10px)")
        .transition()
        .duration(200)
        .remove();
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    },

    formatCurrencyMobile(amount) {
      if (amount >= 1000000000) {
        return `$${(amount / 1000000000).toFixed(1)}B`;
      } else if (amount >= 1000000) {
        return `$${(amount / 1000000).toFixed(0)}M`;
      } else if (amount >= 1000) {
        return `$${(amount / 1000).toFixed(0)}K`;
      } else {
        return `$${amount.toFixed(0)}`;
      }
    },

    handleResize() {
      this.initChart();
      this.renderChart();
    },
  },
};
</script>

<style lang="scss" scoped>

.gmv-chart {
  width: 100%;
  height: 100%;
  flex: 1;
  box-sizing: border-box;
  position: relative;
  overflow: visible;
}

.chart-header {
  margin-bottom: 40px;
  text-align: center;
  position: relative;
}

.chart-title {
  font-family: 'Inter', sans-serif;
  font-size: 28px;
  font-weight: 800;
  color: #1F2937;
  margin: 0 0 12px 0;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.5px;
}

.chart-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  color: #6B7280;
  margin: 0;
  font-weight: 500;
  letter-spacing: 0.3px;
}

.chart-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: visible;
  box-sizing: border-box;
  padding: 20px;
}

.chart-container svg {
  width: 100%;
  height: auto;
  border-radius: 12px;
}

/* Global tooltip styles */
:global(.d3-tooltip) {
  z-index: 1000;
}

@media (max-width: 800px) {
  .gmv-chart {
    padding: 0px;
    flex: 1;
    height: 100%;
  }

  .chart-title {
    font-size: 24px;
  }

  .chart-section{
    margin: 0;
  }

  .chart-subtitle {
    font-size: 14px;
  }

  .dashboard-content{
    flex: 1;
  }

  .chart-container {
    padding: 4px;
    min-height: 350px;
    width: 100%;
    flex: 1;
    overflow: visible;
  }

  .chart-container svg {
    overflow: visible;
  }
}
</style>
